import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { CampaignRebateTable } from '@pages/Traders-Room/Campaign-Results/Rebate/CampaignRebateTable'
import {
  useCampaignRebateFetch,
  useCampaignRebateResultsFetch,
} from '@pages/Traders-Room/Campaign-Results/Rebate/hooks/useCampaignRebateFetch'
import { useCampaignRebateFile } from '@pages/Traders-Room/Campaign-Results/Rebate/hooks/useCampaignRebateFile'
import { useCampaignRebateSearch } from '@pages/Traders-Room/Campaign-Results/Rebate/hooks/useCampaignRebateSearch'
import { useCampaignRebateSort } from '@pages/Traders-Room/Campaign-Results/Rebate/hooks/useCampaignRebateSort'
import { useCampaignRebateSortSettings } from '@pages/Traders-Room/Campaign-Results/Rebate/hooks/useCampaignRebateSortSettings'

import { useModal } from '@context/ModalsContext'

import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'

import { PageQuery } from '@utils/ApiClient'
import { useWindowResize } from '@utils/domUtils'

import { ClientCampaignRebateResultDto } from '@model/ClientCampaignRebateResultDto'
import { ClientRebateCampaignDetailDto } from '@model/ClientRebateCampaignDetailDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import {
  ScrollToIds,
  useScrollAfterLoad,
  useScrollToElementIds,
} from '@hooks/useScrollToElementIds'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { Notice } from '@shared/components/Notice'
import { InformationModal as SharedInformationModal } from '@shared/modals/Information.modal'

import { CampaignRebateCards } from './CampaignRebateCard'

export const CampaignRebatePage: React.FC = () => {
  useScrollToTop()
  const { accountId, campaignId } = useParams<{ accountId: string; campaignId: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { formatMoney } = useFormatNumber()
  const { scrollIntoView } = useScrollToElementIds()

  const sort = useCampaignRebateSort()
  const search = useCampaignRebateSearch(sort)

  const campaignResults = useCampaignRebateResultsFetch(accountId, campaignId, search)
  const campaignDetails = useCampaignRebateFetch(accountId, campaignId)

  const sortSettings = useCampaignRebateSortSettings(campaignDetails.query.data, sort)

  const query = campaignResults.query
  const { meta, pageQuery, setPageQuery } = query

  const generatedDocumentFile = useCampaignRebateFile({
    data: query.data,
    campaignDetail: campaignDetails.query.data,
  })

  const currencyId =
    campaignDetails.query.data?.tradingAccount?.currencyId ??
    campaignResults.query.data?.[0]?.currency?.id ??
    'USD'

  const infoModal = useModal(SharedInformationModal, {
    title: t('Campaigns.Paid Out Rebate'),
    secondaryLabel: t('Got It'),
  })
  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = useState(false)

  const openPaidOutInfo = () =>
    infoModal.open({
      description: () => (
        <p>{t('Campaigns.amounts under', { amount: formatMoney(1, currencyId) })}</p>
      ),
    })

  const subtitle = useMemo(() => {
    const platformTypeName = campaignDetails.query.data?.tradingAccount?.platformType?.name
    return platformTypeName ? ` | ${platformTypeName}` : ''
  }, [campaignDetails.query.data?.tradingAccount])

  useEffect(() => {
    if (isPaginationEntrySelected && !campaignResults.query.isLoading) {
      scrollIntoView([ScrollToIds.CampaignTotalRebateHeader])
      setIsPaginationEntrySelected(false)
    }
  }, [campaignResults.query.isLoading, isPaginationEntrySelected, scrollIntoView])

  useScrollAfterLoad(
    ScrollToIds.CampaignTotalRebateHeader,
    campaignResults.query.isLoading,
    meta?.pageSize
  )

  const handlePageChange = (pageIndex: number, pageSize?: number) => {
    if (pageSize && pageSize !== meta?.pageSize) {
      setIsPaginationEntrySelected(true)
    }
    return setPageQuery!({ ...pageQuery, pageIndex, pageSize })
  }

  const handleSortChange = useCallback(
    (next?: PageQuery) => {
      if (!next) return
      search.setPageQuery(next)
    },
    [search.setPageQuery]
  )

  return (
    <>
      <Loading
        showLoadingIcon
        isLoading={campaignDetails.query.isLoading || campaignResults.query.isLoading}
      >
        <PageSection
          id={ScrollToIds.CampaignTotalRebateHeader}
          title={`${t('Tabs.Campaign Results')} / ${campaignDetails.query.data?.name || ''}`}
          renderMessage={() =>
            `${t('Campaigns.Total Rebate')}: ${formatMoney(
              campaignDetails?.query.data?.totalRebate,
              campaignDetails.query.data?.tradingAccount.currencyId ?? undefined
            )}${subtitle}`
          }
          onGoBack={() => navigate(-1)}
          sortOptions={sortSettings.sortOptions}
          pageQuery={search.pageQuery}
          setPageQuery={handleSortChange}
          documentFile={campaignResults.query.hasInitialResults ? generatedDocumentFile : undefined}
        />
        {campaignDetails.query?.data?.disqualified && (
          <Box pt='x8'>
            <Notice
              id={'disqualified'}
              message={t("Campaigns.You've been disqualified from the competition")}
              type={'warning'}
            />
          </Box>
        )}

        <Loading isLoading={campaignResults.query.isLoading} showLoadingIcon>
          {campaignDetails.query.data && (
            <CampaignContestRecords
              data={campaignResults.query.data}
              campaignDetail={campaignDetails.query.data}
              sortSettings={sortSettings}
              search={search}
              setInfoModal={(v: boolean) => v && openPaidOutInfo()}
            />
          )}
        </Loading>

        {meta && (
          <Pagination
            scrollToHeaderId={ScrollToIds.CampaignTotalRebateHeader}
            pageData={meta}
            onPageChanged={handlePageChange}
          />
        )}
      </Loading>
    </>
  )
}

interface CampaignContestRecordsProps {
  data: ClientCampaignRebateResultDto[]
  campaignDetail: ClientRebateCampaignDetailDto
  sortSettings: ReturnType<typeof useCampaignRebateSortSettings>
  search: ReturnType<typeof useCampaignRebateSearch>
  setInfoModal(value: boolean): void
}

const CampaignContestRecords: React.FC<CampaignContestRecordsProps> = (props) => {
  const { data, campaignDetail, sortSettings, search, setInfoModal } = props
  const isMobile = useWindowResize()

  if (isMobile) {
    return (
      <CampaignRebateCards
        data={data}
        campaign={campaignDetail}
        campaignRebateValue={sortSettings.getCampaignRebateValue()}
        setInfoModal={setInfoModal}
      />
    )
  }

  return (
    <CampaignRebateTable
      data={data}
      campaign={campaignDetail}
      sortOptions={sortSettings.sortOptions}
      pageQuery={search.pageQuery}
      setPageQuery={search.setPageQuery}
      setInfoModal={setInfoModal}
    />
  )
}
